import { FormProvider, useForm } from 'react-hook-form';
import { useTranslate } from "../../customHooks";
import { RouteUri } from '../../routeUri';
import { useHistory, useLocation } from 'react-router-dom';
import CircularLoader from '../../components/CircularLoader/CircularLoader';
import { SUBSCRIPTION_ACCOUNT_STATUS } from '../../utils/constants';
import { useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { PanwMiniLogoV2 } from '../../images';
import { ImgIcon, SelectItem, Body } from "@panwds/react-ui";
import { Select, FormLayout } from '@panwds/react-form';
import { useSubscriptionParams } from './hooks/useSubscriptionParams';
import { useValidateBillingCode } from './hooks/useValidateBillingCode';
import { useOktaSignIn } from './hooks/useOktaSignIn';
import {
    SubscriptionAccountIdField,
    SubscriptionEmailField,
    HasBillingCodeRadioGroup,
    SubscriptionBillingCodeField
} from './components/FormFields';
import { SubscriptionSubmitButton } from './components/ButtonComponents';
import {
    SUBSCRIPTION_FIELDS,
    SUBSCRIPTION_I18N_KEYS,
    SUBSCRIPTION_METRICS
} from './constants';

export const SubscriptionAccountLinked = (props: any) => {
    const translate = useTranslate();
    const history = useHistory();
    const location = useLocation();
    const { accountId, accountStatus, listing, allParams } = useSubscriptionParams();
    const { validateCode, isValidating: validatingScmCode } = useValidateBillingCode();
    const { signInWithBillingCode, signInWithoutCode } = useOktaSignIn();
    const ONBOARDED_ACCOUNT = accountStatus === SUBSCRIPTION_ACCOUNT_STATUS.ASSOCIATE_SUCCESS;
    const isAirfm = listing === 'AIRFM';
    const isLinkedAccount = accountStatus === SUBSCRIPTION_ACCOUNT_STATUS.LINKED ||
        accountStatus === SUBSCRIPTION_ACCOUNT_STATUS.IN_METERING_GROUP;

    // Parse tsg-ids from query parameters for AIRFM linked accounts
    const tsgIdOptions = useMemo<SelectItem[]>(() => {
        if (!isAirfm || !isLinkedAccount) return [];

        const tsgIdsParam = allParams['tsg-ids'];

        if (!tsgIdsParam) return [];

        const tsgIds = Array.isArray(tsgIdsParam) ? tsgIdsParam : [tsgIdsParam];
        return tsgIds
            .filter(Boolean)
            .map((tsgId) => ({
                value: tsgId as string,
                children: tsgId as string
            }));
    }, [isAirfm, isLinkedAccount, allParams]);

    const formMethods = useForm({});
    const {
        formState: { isValid }, watch
    } = formMethods
    const [redirecting, setRedirecting] = useState(false);

    const handleSubmit = async (params: any = {}) => {
        const { BillingAssociationCode, UserName, AccountId, TsgId } = params;

        if (BillingAssociationCode) {
            const isValidScmCode = await validateCode(
                UserName,
                BillingAssociationCode,
                AccountId,
                listing
            );
            if (isValidScmCode) {
                setRedirecting(true);
                signInWithBillingCode(UserName, BillingAssociationCode);
            }
        } else if (isAirfm && isLinkedAccount && TsgId) {
            // AIRFM linked account without BAC: redirect to SCM with selected TSG ID
            window.open(`${process.env.REACT_APP_SCM_URL}?${TsgId}`, '_blank');
        } else {
            if (ONBOARDED_ACCOUNT) {
                setRedirecting(true);
                signInWithoutCode(UserName);
            } else {
                // Keep the marketplace params so that, after login, the user lands on the
                // link-account step (Login forwards location.search to Register), which calls
                // link-account for this landing instead of going straight to the dashboard.
                setRedirecting(true);
                history.push({ pathname: RouteUri.Login, search: location.search });
            }
        }
    };
    const hasCode = watch(SUBSCRIPTION_FIELDS.HAS_BILLING_ASSOCIATION_CODE);
    useEffect(() => {
        if (props.setHasCode && hasCode) {
            props.setHasCode(hasCode);
        }
    }, [hasCode, props.setHasCode]);

    return (
        <>
            <Body as="div" addClassName="tw-px-4 tw-py-2 tw-space-y-4">
                <Body as="div" addClassName="tw-text-left"><ImgIcon alt="Palo Design System" src={PanwMiniLogoV2} size="3xl"/></Body>
                <Body size="lg" addClassName="tw-font-semibold" style={{ fontSize: '20px' }}>{translate(SUBSCRIPTION_I18N_KEYS.ACCOUNT_LINKED_SUBTITLE)}</Body>
                <Body size="md" style={{ fontSize: '16px' }}>
                    <FormattedMessage
                        id={SUBSCRIPTION_I18N_KEYS.ACCOUNT_LINKED_DESCRIPTION}
                        values={{
                            accountID: accountId
                        }}
                    />
                </Body>
                <FormProvider {...formMethods}>
                    <form onSubmit={formMethods.handleSubmit(handleSubmit)}>
                        <Body as="div" addClassName={`tw-relative ${(validatingScmCode || redirecting) ? "loadingBlur" : ""}`}>
                            {(validatingScmCode || redirecting) && <CircularLoader loading={true} />}
                            <FormLayout>
                            <SubscriptionAccountIdField value={accountId} />

                            <HasBillingCodeRadioGroup />

                            {/* Show email and billing code only when BAC is selected */}
                            {hasCode === "Yes" && (
                                <>
                                    <SubscriptionEmailField />
                                    <SubscriptionBillingCodeField />
                                </>
                            )}

                            {/* For AIRFM linked without BAC, show TSG select */}
                            {hasCode === "No" && isAirfm && isLinkedAccount && tsgIdOptions.length > 0 && (
                                <Select
                                    label="TSG ID"
                                    name="TsgId"
                                    items={tsgIdOptions}
                                    requiredAsterisk
                                    dataMetrics="airfm-linked-tsg-id-select"
                                    dataTestId="airfm-linked-tsg-id-select"
                                    simpleFormat
                                    placeholder=""
                                />
                            )}

                            <SubscriptionSubmitButton
                                dataMetrics={SUBSCRIPTION_METRICS.ACCOUNT_LINKED_CONTINUE}
                                variant="standard"
                                disabled={!isValid}
                            >
                                {translate(SUBSCRIPTION_I18N_KEYS.CONTINUE)}
                            </SubscriptionSubmitButton>
                        </FormLayout>
                        </Body>
                    </form>
                </FormProvider>
            </Body>
        </>
    );
};
